import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Crown, Handshake, Target, Globe2, TrendingUp, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import FadeIn from "../components/FadeIn";
import Stat from "../components/Stat";
import { VALUES, STATS } from "../mock";
import useSEO from "../hooks/useSEO";

const valueIcons = { INTEGRITY: ShieldCheck, LEADERSHIP: Crown, RELIABILITY: Handshake };

const About = () => {
  useSEO({
    title: "About us — Dubai Commodity Trader",
    description: "Prime Trade L.L.C — FZ is a UAE-based commodity trading company headquartered in Dubai. Trusted by global producers and buyers across 30+ countries.",
  });
  return (
    <main>
      <PageHero title="About us" eyebrow="Who we are" breadcrumbs={[{ label: "About us" }]} />

      <section className="pt-section">
        <div className="max-w-[1280px] mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
          <FadeIn className="lg:col-span-6">
            <img src="https://images.unsplash.com/photo-1462899006636-339e08d1844e?auto=format&fit=crop&w=1200&q=80" alt="Corporate" className="w-full h-[520px] object-cover" />
          </FadeIn>
          <FadeIn className="lg:col-span-6" delay={120}>
            <div className="pt-eyebrow">About the company</div>
            <h2 className="pt-h2">Energising global trade</h2>
            <div className="pt-divider" />
            <p className="text-[15px] leading-relaxed text-[#4a4a4a] mb-5">
              Prime Trade L.L.C — FZ is a UAE-based commodity trading company headquartered in Dubai. We connect producers and consumers of vital resources — from agricultural staples to industrial raw materials — across more than 30 countries on five continents.
            </p>
            <p className="text-[15px] leading-relaxed text-[#4a4a4a] mb-5">
              Our integrated approach combines deep sector expertise, robust logistics partnerships and a disciplined risk culture. We work with major global brands and proven local entrepreneurs alike, building long-term relationships founded on integrity and performance.
            </p>
            <p className="text-[15px] leading-relaxed text-[#4a4a4a]">
              From sourcing and trading to logistics and delivery, our team works around the clock to ensure that the materials our customers need arrive on time, on spec and at a fair price.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="pt-section pt-section-light">
        <div className="max-w-[1280px] mx-auto px-6">
          <FadeIn>
            <div className="text-center">
              <div className="pt-eyebrow">Our values</div>
              <h2 className="pt-h2">Principles that guide us</h2>
              <div className="pt-divider mx-auto" />
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {VALUES.map((v, idx) => {
              const Icon = valueIcons[v.key];
              return (
                <FadeIn key={v.key} delay={idx * 100}>
                  <div className="pt-value-card h-full">
                    <div className="icon-wrap"><Icon size={24} /></div>
                    <h3 className="text-[20px] font-semibold text-[#2c2c2c] mb-2">{v.key}</h3>
                    <p className="text-[14px] text-[#4a4a4a] leading-relaxed">{v.text}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pt-section-dark py-14">
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATS.map((s) => (
            <Stat key={s.label} {...s} />
          ))}
        </div>
      </section>

      <section className="pt-section">
        <div className="max-w-[1280px] mx-auto px-6">
          <FadeIn>
            <div className="text-center max-w-2xl mx-auto">
              <div className="pt-eyebrow">Our approach</div>
              <h2 className="pt-h2">How we create value</h2>
              <div className="pt-divider mx-auto" />
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { i: Target, t: "Sourcing Excellence", d: "Direct relationships with producers and refiners across key origins, ensuring quality, traceability and reliable supply." },
              { i: Globe2, t: "Global Logistics", d: "Optimised vessel chartering, port operations and inland transportation backed by trusted partners worldwide." },
              { i: TrendingUp, t: "Market Intelligence", d: "Real-time market analysis, risk management and price optimisation across every commodity vertical we trade." },
            ].map((b, i) => {
              const Ic = b.i;
              return (
                <FadeIn key={b.t} delay={i * 100}>
                  <div className="pt-value-card h-full">
                    <div className="icon-wrap"><Ic size={24} /></div>
                    <h3 className="text-[20px] font-semibold text-[#2c2c2c] mb-2">{b.t}</h3>
                    <p className="text-[14px] text-[#4a4a4a] leading-relaxed">{b.d}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link to="/contacts" className="btn-red">Work with us <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
